import { c as _c } from "react/compiler-runtime";
import { Button, Checkbox, CountrySelect, countryHasRegions, Form, Input, Select, SelectItem, Spinner, StateSelect, TouchableTooltip } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import { Turnstile } from "@marsidev/react-turnstile";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Controller, useWatch } from "react-hook-form";
import { CheckCircleBold, DangerBold, InfoCircleLinear, LockKeyholeLinear } from "solar-icon-set";
import { OFFERING_TERMS_URL } from "@/lib/offeringTerms";
import { useRegisterTrialPublicForm } from "./hooks/useRegisterTrialPublicForm/useRegisterTrialPublicForm";
import { PUBLIC_TRIAL_DETAILS, TURNSTILE_SITE_KEY } from "./register-public.constants";
import type { RegisterTrialPublicFormProps } from "./register-public.types";
import { SUPPORT_EMAIL } from "./TrialSetupReportedNotice";
export function RegisterTrialPublicForm({
  controller,
  identityPrefill,
  hideFields
}: RegisterTrialPublicFormProps) {
  const t = useTranslations("trials.register_public");
  const {
    isSubmitting,
    trialSignupOutcome
  } = controller;
  const {
    companyRoles,
    form: {
      control,
      handleSubmit,
      setValue,
      formState: {
        isSubmitting: isFormSubmitting
      }
    },
    onSubmit,
    turnstile
  } = useRegisterTrialPublicForm(controller, identityPrefill);
  const companyRole = useWatch({
    control,
    name: "companyRole"
  });
  const country = useWatch({
    control,
    name: "country"
  });
  const regionRequired = countryHasRegions(country);
  const submitting = isSubmitting || isFormSubmitting;
  if (trialSignupOutcome?.kind === "ineligible") {
    if (isAlreadyActiveTrialStatus(trialSignupOutcome.status)) {
      return <div aria-live="polite" className="flex flex-col items-center gap-4 text-center">
          <CheckCircleBold aria-hidden className="size-16 text-bg-indicator-success!" />
          <h1 className="font-semibold text-2xl leading-tight md:text-3xl">{t("already_active.title")}</h1>
          <p className="max-w-sm text-muted-foreground text-sm">{t("already_active.body")}</p>
          <Button as={Link} color="primary" href="/resolve?returnTo=files">
            {t("already_active.continue")}
          </Button>
        </div>;
    }
    return <div aria-live="polite" className="flex flex-col items-center gap-4 text-center" role="alert">
        <DangerBold aria-hidden className="size-16 text-bg-indicator-danger!" />
        <h1 className="font-semibold text-2xl leading-tight md:text-3xl">{t("ineligible.title")}</h1>
        <p className="max-w-sm text-muted-foreground text-sm">
          {t("ineligible.body", {
          status: trialSignupOutcome.status,
          email: SUPPORT_EMAIL
        })}
        </p>
      </div>;
  }
  if (trialSignupOutcome?.kind === "failed") {
    return <div aria-live="polite" className="flex flex-col items-center gap-4 text-center" role="alert">
        <DangerBold aria-hidden className="size-16 text-bg-indicator-danger!" />
        <h1 className="font-semibold text-2xl leading-tight md:text-3xl">{t("failed.title")}</h1>
        <p className="max-w-sm text-muted-foreground text-sm">{trialSignupOutcome.message}</p>
      </div>;
  }
  if (hideFields) {
    return <div aria-live="polite" className="flex flex-col items-center gap-4 text-center">
        <Spinner size="lg" />
        <h1 className="font-semibold text-2xl leading-tight md:text-3xl">{t("setting_up.title")}</h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t("setting_up.body")}</p>
      </div>;
  }
  return <div className="flex flex-col gap-6">
      <div className="space-y-2">
        <h1 className="font-semibold text-2xl leading-tight md:text-3xl">{t("title")}</h1>
        <p className="text-muted-foreground text-sm">{t("subtitle")}</p>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-lg bg-default-100 px-4 py-3 text-default-600 text-sm">
        {PUBLIC_TRIAL_DETAILS.map(({
        label,
        Icon
      }) => <div className="flex items-center gap-2" key={label}>
            <Icon aria-hidden className="size-4 shrink-0" />
            <span>{t(`details.${label}`)}</span>
          </div>)}
      </div>

      <Form className="flex flex-col gap-5 pb-24 md:pb-0" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid w-full gap-5 sm:grid-cols-2">
          <Controller control={control} name="firstName" render={({
          field,
          fieldState: {
            error,
            invalid
          }
        }) => <Input {...field} errorMessage={error?.message} isInvalid={invalid} label={t("fields.first_name")} maxLength={100} placeholder={t("fields.first_name_placeholder")} validationBehavior="aria" variant="bordered" />} />
          <Controller control={control} name="lastName" render={({
          field: field_0,
          fieldState: {
            error: error_0,
            invalid: invalid_0
          }
        }) => <Input {...field_0} errorMessage={error_0?.message} isInvalid={invalid_0} label={t("fields.last_name")} maxLength={100} placeholder={t("fields.last_name_placeholder")} validationBehavior="aria" variant="bordered" />} />
        </div>

        <Controller control={control} name="email" render={({
        field: field_1,
        fieldState: {
          error: error_1,
          invalid: invalid_1
        }
      }) => <Input {...field_1} autoComplete="email" errorMessage={error_1?.message} inputMode="email" isInvalid={invalid_1} label={t("fields.email")} maxLength={254} placeholder={t("fields.email_placeholder")} validationBehavior="aria" variant="bordered" />} />

        <Controller control={control} name="company" render={({
        field: field_2,
        fieldState: {
          error: error_2,
          invalid: invalid_2
        }
      }) => <Input {...field_2} errorMessage={error_2?.message} isInvalid={invalid_2} label={t("fields.company")} maxLength={100} placeholder={t("fields.company_placeholder")} validationBehavior="aria" variant="bordered" />} />

        <Controller control={control} name="phone" render={({
        field: field_3,
        fieldState: {
          error: error_3,
          invalid: invalid_3
        }
      }) => <Input {...field_3} autoComplete="tel" errorMessage={error_3?.message} inputMode="tel" isInvalid={invalid_3} label={t("fields.phone")} maxLength={50} placeholder={t("fields.phone_placeholder")} validationBehavior="aria" variant="bordered" />} />

        <div className={cn("grid w-full gap-5", regionRequired && "sm:grid-cols-2")}>
          <Controller control={control} name="country" render={({
          field: {
            name,
            value,
            onChange,
            onBlur
          },
          fieldState: {
            error: error_4,
            invalid: invalid_4
          }
        }) => <CountrySelect errorMessage={error_4?.message} isInvalid={invalid_4} name={name} onBlur={onBlur} onChange={next => {
          onChange(next ?? "");
          // The previous region almost never exists under the new country.
          setValue("state", "", {
            shouldValidate: false
          });
        }} value={value} />} />
          {regionRequired ? <Controller control={control} name="state" render={({
          field: {
            name: name_0,
            value: value_0,
            onChange: onChange_0,
            onBlur: onBlur_0
          },
          fieldState: {
            error: error_5,
            invalid: invalid_5
          }
        }) => <StateSelect country={country} errorMessage={error_5?.message} isInvalid={invalid_5} name={name_0} onBlur={onBlur_0} onChange={next_0 => onChange_0(next_0 ?? "")} value={value_0} />} /> : null}
        </div>

        {companyRoles.length > 0 ? <div className="w-full">
            <Controller control={control} name="companyRole" render={({
          field: {
            name: name_1,
            value: value_1,
            onChange: onChange_1,
            ref
          },
          fieldState: {
            error: error_6,
            invalid: invalid_6
          }
        }) => <Select ref={ref} errorMessage={error_6?.message} isInvalid={invalid_6} label={t("fields.role")} name={name_1} onSelectionChange={keys => onChange_1(Array.from(keys)[0] as string || undefined)} placeholder={t("fields.role_placeholder")} selectedKeys={value_1 ? [value_1] : []} validationBehavior="aria" variant="bordered">
                  {companyRoles.map(role => <SelectItem key={role}>{role}</SelectItem>)}
                </Select>} />
          </div> : null}

        {companyRole === "Other" ? <Controller control={control} name="customCompanyRole" render={({
        field: field_4,
        fieldState: {
          error: error_7,
          invalid: invalid_7
        }
      }) => <Input {...field_4} errorMessage={error_7?.message} isInvalid={invalid_7} label={t("fields.custom_role")} maxLength={100} placeholder={t("fields.custom_role_placeholder")} validationBehavior="aria" variant="bordered" />} /> : null}

        <Controller control={control} name="acceptedTerms" render={({
        field: {
          name: name_2,
          value: value_2,
          onChange: onChange_2
        },
        fieldState: {
          error: error_8,
          invalid: invalid_8
        }
      }) => <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Checkbox classNames={{
            label: "text-sm"
          }} isInvalid={invalid_8} isSelected={value_2} name={name_2} onValueChange={onChange_2} size="sm">
                  {t("terms.agree")}
                </Checkbox>
                <Link className="text-primary text-sm hover:underline" href={OFFERING_TERMS_URL} rel="noopener noreferrer" target="_blank">
                  {t("terms.link")}
                </Link>
              </div>
              {error_8 && <p className="text-danger-500 text-xs">{error_8.message}</p>}
            </div>} />

        <Controller control={control} name="marketingOptIn" render={({
        field: {
          name: name_3,
          value: value_3,
          onChange: onChange_3
        }
      }) => <div className="flex items-center gap-1">
              <Checkbox classNames={{
          label: "text-sm"
        }} isSelected={value_3} name={name_3} onValueChange={onChange_3} size="sm">
                {t("marketing.opt_in")}
              </Checkbox>
              <TouchableTooltip ariaLabel={t("marketing.tooltip_label")} color="foreground" content={<p className="max-w-xs text-xs leading-relaxed">{t("marketing.consent_detail")}</p>}>
                <InfoCircleLinear className="text-muted-foreground" size={16} />
              </TouchableTooltip>
            </div>} />

        <div className="w-full space-y-1">
          <div className="flex min-h-16 w-full justify-center overflow-hidden rounded-lg border border-default-200 bg-content2/40 p-1">
            <Turnstile ref={turnstile.ref} onError={turnstile.onError} onExpire={turnstile.onExpire} onSuccess={turnstile.onSuccess} options={{
            action: "public_trial_registration",
            size: "flexible",
            theme: "auto"
          }} siteKey={TURNSTILE_SITE_KEY ?? ""} />
          </div>
          {turnstile.error ? <p className="text-danger-500 text-xs">{turnstile.error}</p> : null}
        </div>

        <RegisterTrialPublicFormFooter submitting={submitting} submissionEnabled={Boolean(turnstile.token)} />

        <div className="flex items-start gap-2 text-default-600 text-xm">
          <LockKeyholeLinear aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span className="flex flex-col">{t("soc2")}</span>
        </div>
      </Form>
    </div>;
}
function isAlreadyActiveTrialStatus(status: string): boolean {
  return status === "TRIAL_SUBSCRIBED" || status === "ACTIVE_TRIAL";
}
const RegisterTrialPublicFormFooter = t0 => {
  const $ = _c(7);
  const {
    submitting,
    submissionEnabled
  } = t0;
  const t = useTranslations("trials.register_public");
  const t1 = !submissionEnabled;
  let t2;
  if ($[0] !== submitting || $[1] !== t) {
    t2 = submitting ? t("submitting") : t("submit");
    $[0] = submitting;
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== submitting || $[4] !== t1 || $[5] !== t2) {
    t3 = <div className="fixed inset-x-0 bottom-0 z-30 w-full border-default-200 border-t bg-content1/95 px-6 py-3 shadow-lg backdrop-blur md:static md:border-0 md:bg-transparent md:p-0 md:shadow-none"><Button className="w-full" color="primary" isDisabled={t1} isLoading={submitting} type="submit">{t2}</Button></div>;
    $[3] = submitting;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};